import React, {useEffect, useState} from "react";
import TestResultSymbol from "./TestResultSymbol";
import moment from "moment";
import InstallService from "../../../services/install.service";
import MobileInstallCheckService from "../../../services/mobileInstallCheck.service";
import DeviceService from "../../../services/device.service";
import Snackbar from "@mui/material/Snackbar";
import Pagination from "@mui/material/Pagination";
import AssetService from "../../../services/asset.service";

const noDevicesMessage = "No devices were found for this install.";
const errorMessage = "There was a problem running the install check.";
const noInstallRecordMessage = "No install record found.";

function HistoricalInstallCheckModal({ show, onClick, macId }) {
    const [isMounted, setIsMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const [install, setInstall] = useState(null);
    const [data, setData] = useState([]);

    const [assetIdDict, setAssetIdDict] = useState({});
    const [assetPropertyList, setAssetPropertyList] = useState([]);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const rowsPerPage = 6;
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(data.length / rowsPerPage);
    const paginatedRows = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    useEffect(() => {
        setIsMounted(true);

        return () => {
            setIsMounted(false);
        }
    }, []);

    useEffect(() => {
        if (isMounted && show && macId && install == null) {
            setIsLoading(true);

            InstallService.getinstallbymacid(macId).then(response => {
                const installId = response.id;

                if (installId && installId > 0) {
                    setInstall(response);
                } else {
                    setIsLoading(false);
                }
            });
        } else if (!show) {
            setInstall(null);
            setData([]);
            setCurrentPage(1);
            setAssetIdDict({});
            setAssetPropertyList([]);
            setIsLoading(false);
        }
    }, [show]);

    useEffect(() => {
        if (isMounted && install != null && install.id) {
            setIsLoading(true);

            getAssetIdsAsync().then(async assetIds => {
                const map = new Map(Object.entries(assetIds));
                setAssetIdDict(map);
                setIsLoading(false);
            })
        }
    }, [install]);

    useEffect(() => {
        if (isMounted && assetIdDict != null && assetIdDict.size > 0) {
            fetchDataAsync().then();
        }
    }, [assetIdDict]);

    const fetchDataAsync = async () => {
        setIsLoading(true);

        if (install != null) {
            InstallService.gethistoricalinstallchecks(install.id).then(async response => {
                if (assetIdDict != null && assetIdDict.size > 0) {
                    await AssetService.getassetsproperties(Array.from(assetIdDict.keys())).then(response => {
                        setAssetPropertyList(response.data);
                    });
                }

                setData(response.data);

                setIsLoading(false);
            }).catch(e => {
                console.log(e);
                setIsLoading(false);
            });
        } else {
            setIsLoading(false);
            showSnackbarMessage(noInstallRecordMessage);
        }
    }

    const runInstallCheckAsync = async () => {
        if (isMounted) {
            setIsLoading(true);

            try {
                if (install == null) {
                    setIsLoading(false);
                    showSnackbarMessage(noInstallRecordMessage);
                } else if (assetIdDict.size === 0) {
                    setIsLoading(false);
                    showSnackbarMessage(noDevicesMessage);
                } else {
                    await Promise.all(
                        Array.from(assetIdDict.keys()).map(id => MobileInstallCheckService.getinstallcheck(id))
                    );

                    await fetchDataAsync();
                }
            } catch (e) {
                console.log(e);
                setIsLoading(false);
                showSnackbarMessage(errorMessage);
            }   
        }
    }

    const getAssetIdsAsync = async () => {
        let assetIdDict = {};

        if (install != null) {
            if (install.registeredDevices == null || install.registeredDevices === "[]") {
                let deviceIds = [];
                let request = JSON.parse(install.requestPayload);
                let configs = request?.CurrentGatewayConfig?.lpxs;

                if (configs != null) {
                    for (let i = 0; i < configs.length; i++) {

                        let assets = configs[i].assets;

                        if (assets != null) {
                            for (let j = 0; j < assets.length; j++) {
                                let cuo = assets[j].cuo;

                                if (cuo != null) {
                                    deviceIds.push(cuo.DeviceID);
                                }
                            }
                        }
                    }
                } else {
                    // assume pro app config
                    deviceIds.push(`${macId}-1-1`);
                    deviceIds.push(`${macId}-2-1`);
                }

                if (deviceIds.length > 0) {
                    let deviceStatuses = await DeviceService.getdevicestatuses(deviceIds);

                    if (deviceStatuses !== null && deviceStatuses.data !== null) {
                        if (deviceStatuses.data.length > 0) {
                            deviceStatuses.data.forEach(item => {
                                assetIdDict[item.assetId] = item.deviceId;
                            });
                        }
                    }
                }
            } else {
                let devices = JSON.parse(install.registeredDevices);

                devices.forEach(item => {
                    assetIdDict[item.AssetId] = item.DeviceId;
                });
            }
        }

        return assetIdDict;
    }

    const showSnackbarMessage = (message) => {
        setSnackbarMessage(message);
        setShowSnackbar(true);
    }

    const getAssetProperties = (installCheck) => {
        if (assetIdDict == null || assetPropertyList == null) {
            return null;
        }
        
        let assetId = null;
        
        for (const [id, deviceId] of assetIdDict.entries()) {
            if (deviceId.toLowerCase() === installCheck.deviceId.toLowerCase()) {
                assetId = id;
                break;
            }
        }

        if (!assetId) {
            return null;
        }
        
        return assetPropertyList.find(a => a.assetId === Number(assetId));
    }

    const renderInstallChecks = () => {
        let pagination =
            <div className="display-flex justify-content-center">
                <Pagination count={totalPages} page={currentPage} onChange={onChangePage} showFirstButton={totalPages > 7} showLastButton={totalPages > 7} size="small" />
            </div>;
 
        let tableHeadings =
            <tr className="no-hover">
                <th>Source</th>
                <th>Make</th>
                <th>Model</th>
                <th>DeviceId</th>
                <th>Nickname</th>
                <th>AssetId</th>
                <th>Install Check Date</th>
                <th>Install Check Result</th>
            </tr>

        let tableContent =
            <tbody>
            {paginatedRows.map((installCheck, index) => {
                const props = getAssetProperties(installCheck);
                
                return (
                    <tr className="no-hover" key={`${installCheck.id}-tableRow`} data={index} value={index}>
                        <td className="border-left">{installCheck.source}</td>
                        <td>{props?.make ?? ''}</td>
                        <td>{props?.model ?? ''}</td>
                        <td>{installCheck.deviceId}</td>
                        <td>{installCheck.nickname}</td>
                        <td>{props?.assetId ?? ''}</td>
                        <td>
                            {moment.utc(installCheck.createdDateTime).local().format("M/D/YYYY [at] h:mm A")}
                        </td>
                        <td className="border-right">
                            <TestResultSymbol result={installCheck.testResult}/>
                        </td>
                    </tr>
                );
            })}
            </tbody>

        return (
            <div className="margin-5">
                <table className="table table-striped table-bordered table-sm table-hover" aria-labelledby="tabelLabel">
                    <thead>
                    {tableHeadings}
                    </thead>
                    {tableContent}
                </table>
                {pagination}
            </div>
        );
    }

    const renderButtons = () => {
        return <div className="row no-gutters alerts-tab-wrapper margin-10-20-0 pad-10-0">
            <div className="col pad-right-30 gap-5 display-flex">
                <button className="primary-btn btn-small width-175"
                        disabled={isLoading}
                        type="button"
                        onClick={runInstallCheckAsync}>
                    Run Install Check
                </button>
            </div>
        </div>;
    }

    const onChangePage = (e, page) => {
        setCurrentPage(page);
    };

    return (
        <div className="modal" style={{display: show ? "block" : "none"}}>
            <div className="modal-wrapper"
                 style={{
                     transform: show ? 'translateY(0vh)' : 'translateY(-100vh)',
                     opacity: show ? '1' : '0'
                 }}>

                <div className="modal-header">
                    <h3>Install Check History</h3>
                    <span className="close-modal-btn" onClick={() => onClick()}>×</span>
                </div>

                {data && data.length > 0 ? renderInstallChecks() : isLoading ? null : <p className="margin-5">No results found.</p>}
                { isLoading ? <div className={"dot-flashing margin-left-25 margin-top-10"}></div> : null}
                {renderButtons()}

                <Snackbar
                    open={showSnackbar}
                    message={snackbarMessage}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center' }}
                    autoHideDuration={3000}
                    onClose={(event, reason) => {
                        if (reason === 'clickaway') {
                            return;
                        }
                        setShowSnackbar(false);
                    }} />
            </div>
        </div>
    );
}

export default HistoricalInstallCheckModal;